import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Tag } from '@carbon/react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Money, When } from './format';
import { KET_QUA, Tien, type Bet } from './BetsTable';
import { RiskTags, type BonusCms, type GamesData, type Risk, type WalletData } from '../pages/Player360';
import { cuocTuMoc, lanTienGanNhat, mocTien, vongCuocDaCan, type LanTien, type VongCuoc } from '../pages/walletKiemTra';

// Soi nhanh nguoi choi trong cua so duyet rut (owner 2026-10-06, khach "an vao id nguoi dung la ra thang trang kiem tra"):
// chi doc, cung nguon voi cac tab cua trang nguoi choi (Nap / rut, Vong cuoc, Game va lai lo, Thuong, Ngan hang va khuyen mai,
// Rui ro va ghi chu, Lich su cuoc) va tai khoan dung chung IP / thiet bi cua lenh rut. Khong nut nao doi du lieu.

// ten / ma nguoi choi mo trang nguoi choi o tab moi (khong mo hop lenh cua dong)
export function NguoiChoiLink({ id, ten }: { id: number; ten?: string | null }) {
  return (
    <Link className="cell-link" to={'/users/' + id} target="_blank" rel="noopener noreferrer"
      title="Mở trang người chơi ở tab mới" onClick={(e) => e.stopPropagation()}>
      {(ten ? ten + ' ' : '') + '#' + id}
    </Link>
  );
}

type PromoReg = { id: number; ten_km: string; trang_thai: string };
type ChungMay = { total: number; items: { id: number; user_name: string; khoa: number; ip: boolean; device: boolean }[] };

const KM_MO: Record<string, string> = { cho_nap: 'Chờ nạp', dang_cuoc: 'Đang tính vòng cược' };

function Doc<T>({ q, children }: { q: { isPending: boolean; isError: boolean; data?: T }; children: (d: T) => ReactNode }) {
  if (q.isPending) return <span className="muted">Đang tải…</span>;
  if (q.isError || q.data === undefined) return <span className="muted">Không đọc được</span>;
  return <>{children(q.data)}</>;
}

// cat: lich su nap / rut chi tra 200 lenh moi nhat (walletHistory); lenh khong thay co the nam ngoai do. Lenh cung loai
// ngoai 200 lenh luon cu hon moi lenh trong do, nen moc xem van (lan moi nhat cua hai loai) van dung khi it nhat mot loai thay
function Lan({ l, ten, cat }: { l: LanTien | null; ten: string; cat: boolean }) {
  return l ? <span>{ten} <Money v={l.row.so_tien} /> lúc <When v={l.luc} /></span>
    : <span className="muted">{cat ? ten + ': ngoài 200 lệnh gần nhất' : 'Chưa có ' + ten.toLowerCase()}</span>;
}

const VI_TOI_DA = 200;

export function KiemTraNhanh({ lenhId, userId }: { lenhId: string; userId: number }) {
  const { can, me } = useAuth();
  const xem = can('users.view');
  // tai khoan chung IP / thiet bi: chi founder (owner 2026-10-20), API tra 403 cho vai tro khac nen khong hoi
  const chuSoHuu = me?.role === 'founder';
  const u = `/api/admin/users/${userId}`;
  // cung khoa voi cac tab cua trang nguoi choi
  const vi = useQuery({ queryKey: ['user-wallet', userId], queryFn: () => api.get<WalletData>(u + '/wallet'), enabled: xem });
  const vc = useQuery({ queryKey: ['user-wagering', userId], queryFn: () => api.get<VongCuoc>(u + '/wagering'), enabled: xem, retry: false });
  const game = useQuery({ queryKey: ['user-games', userId, '', ''], queryFn: () => api.get<GamesData>(u + '/games'), enabled: xem });
  const thuong = useQuery({ queryKey: ['user-bonuses', userId], queryFn: () => api.get<{ cms: BonusCms[] }>(u + '/bonuses'), enabled: xem });
  const nh = useQuery({ queryKey: ['user-bank', userId], queryFn: () => api.get<{ promos: PromoReg[] }>(u + '/ngan-hang'), enabled: xem });
  const rr = useQuery({ queryKey: ['user-risk', userId], queryFn: () => api.get<Risk>(u + '/risk'), enabled: xem });
  const chung = useQuery({ queryKey: ['wallet-chung-may', lenhId], queryFn: () => api.get<ChungMay>('/api/admin/wallet/' + lenhId + '/chung-may'), enabled: chuSoHuu, retry: false });
  const cuoc = useQuery({
    queryKey: ['wallet-kt-cuoc', userId],
    queryFn: () => api.get<{ items: Bet[] }>('/api/admin/bets', { userId, limit: 20 }),
    enabled: can('bets.view'),
  });

  const lan = vi.data ? lanTienGanNhat(vi.data.rows, lenhId) : null;
  const moc = lan ? mocTien(lan) : null;
  const van = cuoc.data && vi.data ? cuocTuMoc(cuoc.data.items, moc) : [];

  return (
    <section className="kiem-tra" aria-label="Soi nhanh người chơi">
      <p><strong>Soi nhanh người chơi</strong> <span className="muted">(chỉ xem)</span></p>
      <dl className="facts">
        {xem ? (
          <>
            <dt>Nạp / rút</dt>
            <dd><Doc q={vi}>{(d) => <>
              Đã nạp <Money v={d.totals.deposited} /> ({d.totals.deposits} lần) · đã rút <Money v={d.totals.withdrawn} /> ({d.totals.withdrawals} lần)
              {d.totals.pendingWithdraw ? <> · rút đang chờ <Money v={d.totals.pendingWithdraw} /></> : null}
              <br /><Lan l={lan!.nap} ten="Nạp gần nhất" cat={d.rows.length >= VI_TOI_DA && d.totals.deposits > 0} />
              {' · '}<Lan l={lan!.rut} ten="Rút gần nhất" cat={d.rows.length >= VI_TOI_DA && d.totals.withdrawals > 0} />
            </>}</Doc></dd>
            <dt>Vòng cược</dt>
            <dd><Doc q={vc}>{(w) => {
              const x = vongCuocDaCan(w);
              return <>
                {x.conLai > 0 ? <Tag size="sm" type="red">Còn phải cược <Money v={x.conLai} /></Tag> : <Tag size="sm" type="green">Đã đủ vòng cược</Tag>}
                {' '}Thưởng đã cược <Money v={x.da} /> / <Money v={x.can} />{x.napCon > 0 ? <> · vòng cược nạp còn <Money v={x.napCon} /></> : null}
              </>;
            }}</Doc></dd>
            <dt>Thắng / thua</dt>
            <dd><Doc q={game}>{(g) => <>
              Từ đầu <Money v={g.totals.pl} signed /> · 30 ngày <Money v={g.totals.pl30} signed /> <span className="muted">(tổng cược <Money v={g.totals.bet} />)</span>
            </>}</Doc></dd>
            <dt>Khuyến mãi / giftcode</dt>
            <dd>
              <Doc q={nh}>{(d) => {
                const mo = d.promos.filter((p) => KM_MO[p.trang_thai]);
                return mo.length ? <span>Khuyến mãi {mo.map((p) => <strong key={p.id}>{p.ten_km}: {KM_MO[p.trang_thai]}</strong>)}</span> : <span>Không có khuyến mãi đang chạy</span>;
              }}</Doc>
              {' · '}
              <Doc q={thuong}>{(d) => {
                const gc = d.cms.find((c) => c.sourceType === 'giftcode');
                return gc ? <span>Giftcode gần nhất <Money v={gc.delta} signed unit={gc.currency === 'xu' ? 'xu' : undefined} /> lúc <When v={gc.completedAt ?? gc.createdAt} />{gc.wagering ? ' (có vòng cược)' : ''}</span>
                  : <span>Chưa nhận giftcode</span>;
              }}</Doc>
            </dd>
            <dt>Cờ rủi ro</dt>
            <dd><Doc q={rr}>{(r) => {
              const f = r.flags;
              const co = f.locked || f.withdrawFrozen || !!f.chatMutedUntil || f.lockedGames.length > 0 || !!(r.marks && (r.marks.watchlist || r.marks.suspectedBot || r.marks.suspectedMulti));
              return co ? <span className="tags">
                {f.locked ? <Tag size="sm" type="red">Đã khóa</Tag> : null}
                {f.withdrawFrozen ? <Tag size="sm" type="red">Dừng rút</Tag> : null}
                {f.chatMutedUntil ? <Tag size="sm" type="warm-gray">Cấm chat</Tag> : null}
                {f.lockedGames.length ? <Tag size="sm" type="cool-gray">Khóa {f.lockedGames.length} game</Tag> : null}
                <RiskTags marks={r.marks} />
              </span> : <span>Không có cờ</span>;
            }}</Doc></dd>
          </>
        ) : null}
        {chuSoHuu ? <>
          <dt>Chung IP / thiết bị</dt>
          <dd><Doc q={chung}>{(c) => c.total ? <span className="tags">
            {c.total} tài khoản:{' '}
            {c.items.map((a) => <span key={a.id}>
              <NguoiChoiLink id={a.id} ten={a.user_name} /> {a.device ? <Tag size="sm" type="purple">thiết bị</Tag> : null}{a.ip ? <Tag size="sm" type="cool-gray">IP</Tag> : null}
              {a.khoa ? <Tag size="sm" type="red">Đã khóa</Tag> : null}
            </span>)}
            {c.total > c.items.length ? <span className="muted">và {c.total - c.items.length} tài khoản khác</span> : null}
          </span> : <span>Không có tài khoản nào</span>}</Doc></dd>
        </> : null}
      </dl>
      {can('bets.view') ? (
        <div className="kiem-tra__cuoc">
          <p>
            <strong>Lịch sử chơi</strong>{' '}
            <span className="muted">{xem ? (moc !== null ? <>{van.length} ván gần nhất từ lần nạp / rút gần nhất (<When v={moc} />)</> : '20 ván gần nhất') : '20 ván gần nhất'}</span>
            {' · '}<Link className="cell-link" to={'/users/' + userId + '?tab=cuoc'} target="_blank" rel="noopener noreferrer">Xem thêm</Link>
          </p>
          {cuoc.isPending || (xem && vi.isPending) ? <p className="muted">Đang tải…</p>
            : cuoc.isError ? <p className="muted">Không đọc được lịch sử cược</p>
              : (xem ? van : cuoc.data.items).length ? (
                <table className="cds--data-table cds--data-table--sm">
                  <thead><tr><th>Lúc</th><th>Game</th><th>Cược</th><th>Thắng / thua</th></tr></thead>
                  <tbody>
                    {(xem ? van : cuoc.data.items).map((b) => {
                      const [ten, mau] = KET_QUA[b.ket_qua] ?? ['?', 'gray'];
                      return (
                        <tr key={b.key}>
                          <td data-label="Lúc"><When v={Number(b.created_time)} /></td>
                          <td data-label="Game">{b.game_name}</td>
                          <td data-label="Cược"><Tien r={b} v={b.game_stake} /></td>
                          <td data-label="Thắng / thua">{b.ket_qua === 'cho' ? null : <Tien r={b} v={b.net} signed />} <Tag size="sm" type={mau}>{ten}</Tag></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : <p className="muted">Không có ván nào từ lần nạp / rút gần nhất.</p>}
        </div>
      ) : null}
    </section>
  );
}

